import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

export default function GoodsReceived() {
  const { user, profile, isAdminOrManager } = useAuth()
  const [grns, setGrns] = useState([])
  const [pos, setPOs] = useState([])
  const [tab, setTab] = useState('list')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg] = useState(null)

  const emptyItem = () => ({ item_name:'', qty_ordered:'', qty_received:'', qty_rejected:0, unit:'', condition:'Good', notes:'' })
  const [form, setForm] = useState({
    po_id:'', supplier:'', received_date: ugandaDate(),
    delivery_note_ref:'', landed_cost:'', is_partial:false,
    stores_notes:'', items:[emptyItem()], proof_url:''
  })

  useEffect(() => {
    loadGRNs()
    supabase.from('purchase_orders').select('id, po_number, supplier, items').eq('is_approved', true).then(({ data }) => setPOs(data || []))
  }, [])

  const loadGRNs = async () => {
    setLoading(true)
    const { data } = await supabase.from('goods_received_notes')
      .select('*, profiles!received_by(full_name), confirmer:profiles!confirmed_by(full_name)')
      .order('received_date', { ascending:false }).limit(30)
    setGrns(data || [])
    setLoading(false)
  }

  const setItem = (i, field, val) => {
    const items = [...form.items]
    items[i] = { ...items[i], [field]: val }
    setForm(f => ({ ...f, items }))
  }

  const handlePOSelect = (poId) => {
    const po = pos.find(p => p.id === poId)
    if (po) {
      setForm(f => ({
        ...f, po_id: poId, supplier: po.supplier,
        items: (po.items || []).map(i => ({
          item_name: i.description, qty_ordered: i.qty, qty_received:'', qty_rejected:0,
          unit: i.unit || '', condition:'Good', notes:''
        }))
      }))
    } else {
      setForm(f => ({ ...f, po_id: poId }))
    }
  }

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploading(true)
    const fileName = `grn-${Date.now()}-${file.name}`
    const { data, error } = await supabase.storage.from('attachments').upload(fileName, file)
    if (!error) {
      const { data: urlData } = supabase.storage.from('attachments').getPublicUrl(fileName)
      setForm(f => ({ ...f, proof_url: urlData.publicUrl }))
      setMsg({ type:'success', text:'Photo uploaded successfully.' })
    } else {
      setMsg({ type:'error', text:'Upload failed: ' + error.message })
    }
    setUploading(false)
    setTimeout(() => setMsg(null), 3000)
  }

  const handleSave = async () => {
    if (!form.po_id) { setMsg({ type:'error', text:'A GRN must be linked to an approved Purchase Order. Select a PO from the dropdown.' }); return }
    if (!form.supplier) { setMsg({ type:'error', text:'Supplier is required.' }); return }
    if (!form.proof_url) { setMsg({ type:'error', text:'Proof of delivery photo is required before saving.' }); return }
    setSaving(true)
    const grn_number = `GRN-${new Date().getFullYear()}-${Date.now().toString().slice(-4)}`
    const { error } = await supabase.from('goods_received_notes').insert({
      po_id: form.po_id || null,
      received_by: user.id,
      grn_number,
      received_date: form.received_date,
      supplier: form.supplier,
      items: form.items.filter(i => i.item_name),
      is_partial: form.is_partial,
      delivery_note_ref: form.delivery_note_ref,
      proof_url: form.proof_url,
      landed_cost: parseFloat(form.landed_cost) || 0,
      stores_notes: form.stores_notes,
      status: 'Pending Confirmation',
    })
    if (error) { setMsg({ type:'error', text:error.message }); setSaving(false); return }

    // Auto update stock for received items
    for (const item of form.items.filter(i => i.item_name && i.qty_received)) {
      if (item.stock_item_id) {
        await supabase.from('stock_movements').insert({
          stock_item_id: item.stock_item_id, user_id: user.id,
          movement_type:'in', quantity: parseFloat(item.qty_received),
          unit: item.unit, movement_date: form.received_date,
          reason:`GRN ${grn_number} — received from ${form.supplier}`,
          reference_type:'grn'
        })
      }
    }

    setMsg({ type:'success', text:`GRN ${grn_number} saved. Stores must confirm and finance must approve before payment.` })
    setSaving(false); setTab('list'); loadGRNs()
    setTimeout(() => setMsg(null), 5000)
  }

  const handleConfirm = async (id) => {
    await supabase.from('goods_received_notes').update({ status:'Confirmed', confirmed_by:user.id }).eq('id', id)
    loadGRNs()
  }

  return (
    <AppLayout title="Goods Received">
      <div className="section-header">
        <div className="section-icon">📥</div>
        <div className="section-info">
          <h2>Goods Received Notes</h2>
          <p>Record every delivery. Photo proof required. Stores confirms before finance can pay.</p>
        </div>
        <button className="btn btn-primary" style={{ marginLeft:'auto' }} onClick={() => setTab('new')}>+ Record Delivery</button>
      </div>

      {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

      <div className="tabs">
        <button className={`tab ${tab==='list'?'active':''}`} onClick={() => setTab('list')}>📋 All GRNs</button>
        <button className={`tab ${tab==='new'?'active':''}`} onClick={() => setTab('new')}>+ Record Delivery</button>
      </div>

      {tab === 'new' && (
        <div className="card">
          <div className="card-title">Record Goods Received</div>
          {msg && <div className={`alert ${msg.type==='success'?'alert-green':'alert-red'}`}>{msg.text}</div>}

          <div className="grid-2" style={{ marginBottom:14 }}>
            <div className="form-group"><label className="form-label">Link to Purchase Order (optional)</label>
              <select className="form-select" value={form.po_id} onChange={e => handlePOSelect(e.target.value)}>
                <option value="">— Select approved PO (required) —</option>
                {pos.map(po => <option key={po.id} value={po.id}>{po.po_number} — {po.supplier}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Supplier *</label>
              <input className="form-input" value={form.supplier} onChange={e => setForm(f=>({...f,supplier:e.target.value}))} placeholder="Auto-filled from PO" readOnly={!!form.po_id} style={{ background: form.po_id ? '#f8f8f0' : undefined }} />
              {form.po_id && <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:3 }}>Auto-filled from PO — cannot be changed</div>}
            </div>
            <div className="form-group"><label className="form-label">Received Date</label><input className="form-input" type="date" value={form.received_date} onChange={e => setForm(f=>({...f,received_date:e.target.value}))} /></div>
            <div className="form-group"><label className="form-label">Supplier Delivery Note Ref</label><input className="form-input" value={form.delivery_note_ref} onChange={e => setForm(f=>({...f,delivery_note_ref:e.target.value}))} placeholder="e.g. DN-2024-045" /></div>
            <div className="form-group"><label className="form-label">Landed Cost — delivery charge (UGX)</label><input className="form-input" type="number" value={form.landed_cost} onChange={e => setForm(f=>({...f,landed_cost:e.target.value}))} placeholder="0" /></div>
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, padding:12, background:'var(--cream)', borderRadius:8, border:'1.5px solid var(--border)', cursor:'pointer' }} onClick={() => setForm(f=>({...f,is_partial:!f.is_partial}))}>
            <span style={{ fontSize:'1.1rem' }}>{form.is_partial?'✅':'⬜'}</span>
            <span style={{ fontWeight:600, fontSize:'0.85rem' }}>This is a partial delivery — more items still to come</span>
          </div>

          <div style={{ fontWeight:700, marginBottom:10 }}>Items Received</div>
          <div className="table-wrap" style={{ marginBottom:10 }}>
            <table>
              <thead><tr><th>Item</th><th>Qty Ordered</th><th>Qty Received</th><th>Qty Rejected</th><th>Unit</th><th>Condition</th><th>Notes</th><th style={{ width:32 }}></th></tr></thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i}>
                    <td><input className="form-input" placeholder="Item name" value={item.item_name} onChange={e => setItem(i,'item_name',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width:80 }} type="number" value={item.qty_ordered} onChange={e => setItem(i,'qty_ordered',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width:80 }} type="number" value={item.qty_received} onChange={e => setItem(i,'qty_received',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width:80 }} type="number" value={item.qty_rejected} onChange={e => setItem(i,'qty_rejected',e.target.value)} /></td>
                    <td><input className="form-input" style={{ width:70 }} value={item.unit} onChange={e => setItem(i,'unit',e.target.value)} placeholder="unit" /></td>
                    <td>
                      <select className="form-select" style={{ width:100 }} value={item.condition} onChange={e => setItem(i,'condition',e.target.value)}>
                        <option>Good</option><option>Damaged</option><option>Expired</option>
                      </select>
                    </td>
                    <td><input className="form-input" placeholder="Notes" value={item.notes} onChange={e => setItem(i,'notes',e.target.value)} /></td>
                    <td>{form.items.length>1 && <button className="btn-del-row" onClick={() => setForm(f=>({...f,items:f.items.filter((_,j)=>j!==i)}))}>✕</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn btn-secondary btn-sm" style={{ marginBottom:18 }} onClick={() => setForm(f=>({...f,items:[...f.items,emptyItem()]}))}>+ Add Item</button>

          {/* Photo Upload */}
          <div style={{ background:'var(--amber-pale)', border:'2px dashed var(--amber)', borderRadius:10, padding:20, marginBottom:16, textAlign:'center' }}>
            <div style={{ fontWeight:700, marginBottom:6 }}>📸 Proof of Delivery — Required</div>
            <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', marginBottom:12 }}>Take a photo of the delivery note, goods, or both. Payment cannot be approved without this.</div>
            {form.proof_url ? (
              <div>
                <div className="alert alert-green" style={{ display:'inline-flex', marginBottom:8 }}>✅ Photo uploaded successfully</div>
                <div><a href={form.proof_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">View Photo</a></div>
              </div>
            ) : (
              <label style={{ cursor:'pointer' }}>
                <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} style={{ display:'none' }} />
                <span className="btn btn-primary">{uploading ? <><span className="spinner" />Uploading...</> : '📷 Take / Upload Photo'}</span>
              </label>
            )}
          </div>

          <div className="form-group"><label className="form-label">Stores Notes</label><textarea className="form-textarea" style={{ minHeight:60 }} value={form.stores_notes} onChange={e => setForm(f=>({...f,stores_notes:e.target.value}))} placeholder="Any issues with delivery, quality concerns, missing items..." /></div>
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving || !form.proof_url}>{saving?<><span className="spinner" />Saving...</>:'💾 Save GRN'}</button>
          {!form.proof_url && <div style={{ fontSize:'0.75rem', color:'var(--red)', marginTop:6 }}>⚠️ Upload proof of delivery photo before saving</div>}
        </div>
      )}

      {tab === 'list' && (
        <div className="card">
          <div className="card-title">Goods Received History</div>
          {loading ? <div style={{ textAlign:'center', padding:32 }}><span className="spinner dark" /></div>
          : grns.length === 0 ? <div className="empty-state"><div className="empty-icon">📥</div><h3>No deliveries recorded yet</h3></div>
          : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>GRN #</th><th>Date</th><th>Supplier</th><th>Partial?</th><th>Proof</th><th>Status</th>{isAdminOrManager && <th>Action</th>}</tr></thead>
                <tbody>
                  {grns.map(grn => (
                    <tr key={grn.id}>
                      <td style={{ fontFamily:"'DM Mono',monospace", fontWeight:600 }}>{grn.grn_number}</td>
                      <td style={{ fontSize:'0.78rem' }}>{new Date(grn.received_date).toLocaleDateString('en-UG')}</td>
                      <td style={{ fontWeight:600 }}>{grn.supplier}</td>
                      <td>{grn.is_partial ? <span className="badge badge-amber">Partial</span> : <span className="badge badge-green">Full</span>}</td>
                      <td>{grn.proof_url ? <a href={grn.proof_url} target="_blank" rel="noreferrer" style={{ color:'var(--green-mid)', fontSize:'0.78rem' }}>📷 View</a> : '—'}</td>
                      <td><span className={`badge ${grn.status==='Confirmed'?'badge-green':grn.status==='Disputed'?'badge-red':'badge-amber'}`}>{grn.status}</span></td>
                      {isAdminOrManager && <td>{grn.status==='Pending Confirmation' && <button className="btn btn-secondary btn-sm" onClick={() => handleConfirm(grn.id)}>✓ Confirm</button>}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  )
}
